import * as Sentry from "@sentry/react-native";
import Constants from "expo-constants";
import type { ExecutionResult, GraphQLError } from "graphql";
import { createClient } from "graphql-ws";
import type { Client } from "graphql-ws";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppState, Platform } from "react-native";
import {
  CacheConfig,
  Environment,
  GraphQLResponse,
  Network,
  Observable,
  PayloadData,
  RequestParameters,
  ROOT_TYPE,
  RecordSource,
  Store,
  Variables,
} from "relay-runtime";

import { getNativeApiHost } from "../utils/getNativeApiHost";
import { getWebApiHost } from "../utils/getWebApiHost";
import fetchGraphQL from "./fetchGraphQL";
import {
  getNativeSessionKey,
  onNativeSessionLock,
  isNativeSessionLocked,
} from "./nativeSessionPersistence";
import { resolveAccessContext } from "./resolveAccessContext";
import {
  clearSubscriptionDiagnosticsForClient,
  getSubscriptionDiagnosticsContext,
  getSubscriptionDiagnosticsForClient,
  setSubscriptionDiagnosticsForClient,
  SubscriptionDiagnostics,
} from "./subscriptionDiagnostics";
import { SubscriptionClient, SubscriptionManager } from "./subscriptionManager";

// Relay passes a "params" object with the query name and text. So we define a helper function
// to call our fetchGraphQL utility with params.text.

const SUBSCRIPTION_CONNECTION_ACK_TIMEOUT_MS = 10_000;
const SUBSCRIPTION_KEEP_ALIVE_MS = 30_000;
const SUBSCRIPTION_PONG_TIMEOUT_MS = 10_000;

const fetchRelayWithAuthHandler = (
  schemaVersionMismatchHandler: () => void,
  clubSlug?: string | null,
  useStoredClubSlug?: boolean,
  onError?: (errors: unknown) => void
) => {
  async function fetchRelay(
    params: RequestParameters,
    variables: Variables,
    cacheConfig: CacheConfig
  ) {
    return fetchGraphQL({
      text: params.text ?? "",
      requiredNativeSessionGeneration:
        typeof cacheConfig.metadata?.requiredNativeSessionGeneration ===
        "number"
          ? cacheConfig.metadata.requiredNativeSessionGeneration
          : undefined,
      variables,
      schemaVersionMismatchHandler,
      clubSlug,
      useStoredClubSlug,
      onError,
    });
  }

  return fetchRelay;
};

type UseRelayPrivateQueryEnvironmentArgs = {
  schemaVersionMismatchHandler: () => void;
  clubSlug?: string;
  useStoredClubSlug?: boolean;
  onError?: (errors: unknown) => void;
};

/**
 * Creates a Relay environment for a one-off query without starting the private
 * GraphQL subscription client. This is useful for a lightweight preflight that
 * must run before a club mounts its full private Relay environment.
 */
export const useRelayPrivateQueryEnvironment = ({
  schemaVersionMismatchHandler,
  clubSlug,
  useStoredClubSlug = true,
  onError,
}: UseRelayPrivateQueryEnvironmentArgs) => {
  const schemaVersionMismatchHandlerRef = useRef(schemaVersionMismatchHandler);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    schemaVersionMismatchHandlerRef.current = schemaVersionMismatchHandler;
    onErrorRef.current = onError;
  }, [schemaVersionMismatchHandler, onError]);

  const fetchRelay = useMemo(
    () =>
      fetchRelayWithAuthHandler(
        () => schemaVersionMismatchHandlerRef.current(),
        clubSlug,
        useStoredClubSlug,
        (errors) => onErrorRef.current?.(errors)
      ),
    [clubSlug, useStoredClubSlug]
  );

  return useMemo(
    () =>
      new Environment({
        network: Network.create(fetchRelay),
        store: new Store(new RecordSource()),
      }),
    [fetchRelay]
  );
};

const toError = (error: unknown) => {
  return error instanceof Error ? error : new Error(String(error));
};

const toPayloadErrors = (errors?: readonly GraphQLError[]) => {
  return errors?.map((error) => ({
    message: error.message,
    locations: error.locations?.map(({ line, column }) => ({ line, column })),
    path: error.path ? [...error.path] : undefined,
  }));
};

const toGraphQLResponse = (
  result: ExecutionResult<PayloadData, Record<string, unknown>>
): GraphQLResponse => {
  const errors = toPayloadErrors(result.errors);

  if (result.data !== null && result.data !== undefined) {
    return {
      data: result.data,
      errors,
      extensions: result.extensions,
    };
  }

  if (result.data === null && result.extensions && !errors?.length) {
    return {
      data: null,
      extensions: result.extensions,
    };
  }

  return {
    errors:
      errors && errors.length > 0
        ? errors
        : [{ message: "Subscription returned no data" }],
    extensions: result.extensions,
  };
};

const subscribe = (
  request: RequestParameters,
  variables: Variables,
  subscriptionManager: SubscriptionManager
) => {
  return Observable.create<GraphQLResponse>((sink) => {
    const operationName = request.name ?? "anonymous";
    let activeClient: SubscriptionClient | null = null;

    const dispose = subscriptionManager.subscribe(
      {
        query: request.text ?? "",
        operationName: request.name,
        variables,
      },
      {
        next: (data) => {
          const graphQLResponse = toGraphQLResponse(data);
          const fields =
            data.data && typeof data.data === "object"
              ? Object.keys(data.data)
              : [];

          activeClient &&
            getSubscriptionDiagnosticsForClient(
              activeClient
            )?.recordSubscriptionReceived(
              operationName,
              fields,
              Boolean(data.errors?.length)
            );
          sink.next(graphQLResponse);
        },
        error: (error) => {
          const normalizedError = toError(error);
          activeClient &&
            getSubscriptionDiagnosticsForClient(
              activeClient
            )?.recordSubscriptionError(operationName, error);
          sink.error(normalizedError);
        },
        complete: () => sink.complete(),
      },
      {
        onAttached: (client) => {
          activeClient = client;
          getSubscriptionDiagnosticsForClient(
            client
          )?.recordSubscriptionStarted(operationName);
        },
        onDetached: (client) => {
          getSubscriptionDiagnosticsForClient(
            client
          )?.recordSubscriptionStopped(operationName);
          if (activeClient === client) activeClient = null;
        },
      }
    );

    return dispose;
  });
};

const createSubscriptionClient = ({
  wsUrl,
  clubSlug,
}: {
  wsUrl: string;
  clubSlug: string | null;
}) => {
  let pongTimeout: ReturnType<typeof setTimeout> | null = null;
  let client: Client | null = null;
  const diagnostics = new SubscriptionDiagnostics({
    onEvent: (event) => {
      Sentry.addBreadcrumb({
        category: "graphql-ws",
        level:
          event.kind === "connection_error" ||
          event.kind === "subscription_error"
            ? "warning"
            : "info",
        message: event.kind,
        data: event.data,
      });
    },
    onSnapshot: () => {
      Sentry.setContext("graphql_ws", getSubscriptionDiagnosticsContext());
    },
  });

  const clearPongTimeout = () => {
    if (pongTimeout) {
      clearTimeout(pongTimeout);
      pongTimeout = null;
    }
  };

  client = createClient({
    url: wsUrl,
    lazy: true,
    retryAttempts: Infinity,
    shouldRetry: () => Platform.OS === "web" || !isNativeSessionLocked(),
    connectionAckWaitTimeout: SUBSCRIPTION_CONNECTION_ACK_TIMEOUT_MS,
    keepAlive: SUBSCRIPTION_KEEP_ALIVE_MS,
    connectionParams: async () => {
      if (Platform.OS === "web") {
        return {
          session_key: localStorage.getItem("x-whoosh-session-key"),
          club_slug: clubSlug,
        };
      }

      const sessionKey = await getNativeSessionKey();

      return { session_key: sessionKey, club_slug: clubSlug };
    },
    on: {
      connecting: (isRetry) => {
        diagnostics.recordConnecting(isRetry);
      },
      opened: () => {
        diagnostics.recordOpened();
      },
      connected: (_socket, _payload, wasRetry) => {
        diagnostics.recordConnected(wasRetry);
      },
      message: (message) => {
        diagnostics.recordProtocolMessage(message.type);
      },
      closed: (event) => {
        clearPongTimeout();
        const closeEvent = event && typeof event === "object" ? event : null;
        diagnostics.recordClosed(
          closeEvent &&
            "code" in closeEvent &&
            typeof closeEvent.code === "number"
            ? closeEvent.code
            : null,
          closeEvent && "reason" in closeEvent ? closeEvent.reason : null
        );
      },
      error: (error) => {
        diagnostics.recordError(error);
        console.log("connectionerror", error);
      },
      ping: (received) => {
        diagnostics.recordPing(received);

        if (received) return;

        clearPongTimeout();
        pongTimeout = setTimeout(() => {
          client?.terminate();
        }, SUBSCRIPTION_PONG_TIMEOUT_MS);
      },
      pong: (received) => {
        diagnostics.recordPong(received);

        if (received) {
          clearPongTimeout();
        }
      },
    },
  });

  setSubscriptionDiagnosticsForClient(client, diagnostics);
  Sentry.setContext("graphql_ws", getSubscriptionDiagnosticsContext());

  return client;
};

const useSubscriptionClient = (clubSlug: string | null | undefined) => {
  const [connection, setConnection] = useState<{
    context: string | null;
    client: Client;
  } | null>(null);
  const subscriptionClient =
    connection && connection.context === clubSlug ? connection.client : null;

  useEffect(() => {
    if (clubSlug === undefined) return;
    let isActive = true;
    let clientToDispose: Client | null = null;

    const getSubClient = async () => {
      try {
        const configDeployEnv = Constants?.expoConfig?.extra?.eas?.deployEnv;
        const isLocal = configDeployEnv === "local";
        const isE2E = configDeployEnv === "e2e";
        const configAPIBase = Constants?.expoConfig?.extra?.eas?.apiBase;
        const apiBase =
          Platform.OS !== "web"
            ? await getNativeApiHost({ isLocal, configAPIBase })
            : await getWebApiHost({ isE2E });

        const apiHost = !isLocal && !isE2E ? `${apiBase}` : "localhost:4000";
        const protocol =
          apiHost === "localhost:3000" ||
          apiHost === "localhost:4000" ||
          apiHost === "127.0.0.1:3000" ||
          apiHost === "127.0.0.1:4000"
            ? "ws"
            : "wss";

        const wsUrl = `${protocol}://${apiHost}/private/socket/websocket`;
        const newClient = createSubscriptionClient({
          wsUrl,
          clubSlug,
        });
        clientToDispose = newClient;

        if (isActive) {
          setConnection({ context: clubSlug, client: newClient });
        } else {
          clearSubscriptionDiagnosticsForClient(newClient);
          Sentry.setContext("graphql_ws", getSubscriptionDiagnosticsContext());
          void newClient.dispose();
        }
      } catch (error) {
        console.log("connectionerror", error);
      }
    };

    setConnection(null);
    getSubClient();

    return () => {
      isActive = false;
      if (clientToDispose) {
        clearSubscriptionDiagnosticsForClient(clientToDispose);
        Sentry.setContext("graphql_ws", getSubscriptionDiagnosticsContext());
      }
      void clientToDispose?.dispose();
    };
  }, [clubSlug]);

  useEffect(() => {
    if (!subscriptionClient) return undefined;

    const restartSubscriptionConnection = () => {
      subscriptionClient.terminate();
    };

    if (Platform.OS === "web") {
      const restartSubscriptionConnectionWhenVisible = () => {
        if (document.visibilityState === "visible") {
          restartSubscriptionConnection();
        }
      };

      window.addEventListener("online", restartSubscriptionConnection);
      document.addEventListener(
        "visibilitychange",
        restartSubscriptionConnectionWhenVisible
      );

      return () => {
        window.removeEventListener("online", restartSubscriptionConnection);
        document.removeEventListener(
          "visibilitychange",
          restartSubscriptionConnectionWhenVisible
        );
      };
    }

    const removeLockListener = onNativeSessionLock(
      restartSubscriptionConnection
    );
    const appStateSubscription = AppState.addEventListener(
      "change",
      (nextAppState) => {
        if (nextAppState === "active") {
          restartSubscriptionConnection();
        }
      }
    );

    return () => {
      removeLockListener();
      appStateSubscription.remove();
    };
  }, [subscriptionClient]);

  return subscriptionClient;
};

/**
 *
 * We now need to await an api call to get the right subscription env.
 * We use this hook to allow components to abstract away that async.
 */
export const useRelayPrivateEnvironment = ({
  schemaVersionMismatchHandler,
  clubSlug,
  useStoredClubSlug = true,
  useStoredStaffContext = true,
  onError,
}: {
  schemaVersionMismatchHandler: () => void;
  clubSlug?: string;
  useStoredClubSlug?: boolean;
  useStoredStaffContext?: boolean;
  onError?: (errors: unknown) => void;
}) => {
  const [privateEnvironment, setPrivateEnvironment] = useState<{
    context: string | null;
    environment: Environment;
  } | null>(null);
  const [resolvedContext, setResolvedContext] = useState<{
    requested: string | undefined;
    useStoredClubSlug: boolean;
    useStoredStaffContext: boolean;
    value: string | null;
  } | null>(null);
  const accessContext =
    resolvedContext?.requested === clubSlug &&
    resolvedContext?.useStoredClubSlug === useStoredClubSlug &&
    resolvedContext?.useStoredStaffContext === useStoredStaffContext
      ? resolvedContext?.value
      : undefined;

  useEffect(() => {
    let active = true;
    void resolveAccessContext(
      clubSlug,
      useStoredClubSlug,
      useStoredStaffContext
    ).then(
      (value) => {
        if (active)
          setResolvedContext({
            requested: clubSlug,
            useStoredClubSlug,
            useStoredStaffContext,
            value,
          });
      },
      (error: unknown) => {
        if (!active) return;
        Sentry.captureException(error);
        setResolvedContext({
          requested: clubSlug,
          useStoredClubSlug,
          useStoredStaffContext,
          value: null,
        });
      }
    );
    return () => {
      active = false;
    };
  }, [clubSlug, useStoredClubSlug, useStoredStaffContext]);

  const subscriptionManager = useMemo(
    () => new SubscriptionManager(),
    // A context change must discard subscriptions rather than attach them to the new socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessContext]
  );
  const subscriptionClient = useSubscriptionClient(accessContext);
  const schemaVersionMismatchHandlerRef = useRef(schemaVersionMismatchHandler);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    schemaVersionMismatchHandlerRef.current = schemaVersionMismatchHandler;
    onErrorRef.current = onError;
  }, [schemaVersionMismatchHandler, onError]);

  const fetchRelay = useMemo(
    () =>
      fetchRelayWithAuthHandler(
        () => schemaVersionMismatchHandlerRef.current(),
        accessContext ?? null,
        false,
        (errors) => onErrorRef.current?.(errors)
      ),
    [accessContext]
  );

  useEffect(() => {
    if (!subscriptionClient || accessContext === undefined) {
      subscriptionManager.setClient(null);
      setPrivateEnvironment(null);
      return;
    }

    subscriptionManager.setClient(subscriptionClient);

    const subscribeFunction = (
      request: RequestParameters,
      variables: Variables
    ) => {
      return subscribe(request, variables, subscriptionManager);
    };
    const env = new Environment({
      network: Network.create(fetchRelay, subscribeFunction),
      store: new Store(new RecordSource()),
      missingFieldHandlers: missingFieldHandlers as unknown as any,
    });
    setPrivateEnvironment({ context: accessContext, environment: env });

    return () => subscriptionManager.setClient(null);
  }, [accessContext, fetchRelay, subscriptionClient, subscriptionManager]);

  return privateEnvironment && privateEnvironment.context === accessContext
    ? privateEnvironment.environment
    : null;
};

/**
 * This function lets relay know that it can safely fulfil a 'node' query from
 * any object with the same ID. IT seems like relay would always do this by default - but this
 * function is what allows it to work cross query.
 *
 * It essentially says "if we query the root for 'node', then first check the cache for that ID."
 *
 * This makes it relatively safe to just query node(id) for data you need, and relay will
 * be smart enough to only call the backend if we need to
 *
 * Eventually this list of handlers might grow.
 *
 * NOTE: This file is JS rather than TS purely because Relay hasn't typed these missing field handlers
 * well and I was having to use : any all the time anyhow.
 */
const missingFieldHandlers = [
  {
    handle(field: any, record: any, argValues: any) {
      if (
        record != null &&
        record.__typename === ROOT_TYPE &&
        field.name === "node" &&
        argValues.hasOwnProperty("id")
      ) {
        return argValues.id;
      }
      return undefined;
    },
    kind: "linked",
  },
];
